// Single source of truth for "which clinic is this visitor booking at?".
//
// Two localStorage keys are read all over the app:
//   selectedLocationId – the raw id, used when building URLs and API payloads
//   selectedLocation   – the full clinic object, used for the header pill
//
// Both used to be written straight from possibly-undefined values
// (`localStorage.setItem(key, user?.defaultProvider?.id)`,
//  `JSON.stringify(undefined)`), and localStorage coerces those to the *strings*
// "undefined" / "null". Those strings are truthy, so every "has a location been
// picked?" check passed while every provider_id sent to the API was garbage —
// which is why links like /categories/all/undefined rendered without ever
// prompting, and why time slots came back empty. Reading and writing only
// through these helpers keeps that class of bug out.

const ID_KEY = "selectedLocationId";
const CLINIC_KEY = "selectedLocation";

const JUNK = ["", "undefined", "null", "NaN", "false"];

/** A clinic id is usable only when it is a positive integer. */
export const isValidClinicId = (value) =>
  /^[1-9]\d*$/.test(String(value ?? "").trim());

/**
 * URL segments arriving from the website / older shared links can literally be
 * the text "undefined". Treat those as "not provided".
 */
export const isUsableSegment = (value) =>
  !!value && !JUNK.includes(String(value).trim());

/**
 * Is this id one of the clinics we actually offer?
 *
 * A well-formed id is not the same as a real one: website links carry ids from
 * another system entirely (e.g. /categories/wellness/7381), and clinics get
 * deactivated. Those pass every format check and then quietly load an empty
 * page, so anywhere the clinic list is available, membership in it is the real
 * test. Returns false while the list is empty — callers decide whether they can
 * verify yet.
 */
export const clinicExistsIn = (list, id) =>
  Array.isArray(list) && list.some((c) => String(c?.id) === String(id));

/** The chosen clinic id, or null when nothing usable is stored. */
export const getStoredClinicId = () => {
  const raw = localStorage.getItem(ID_KEY);
  return isValidClinicId(raw) ? String(raw).trim() : null;
};

/** The chosen clinic object, or null when nothing usable is stored. */
export const getStoredClinic = () => {
  const raw = localStorage.getItem(CLINIC_KEY);
  if (!raw || JUNK.includes(raw)) return null;
  try {
    const parsed = JSON.parse(raw);
    return isValidClinicId(parsed?.id) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Has the visitor actually picked a location? Only the id is required — a
 * returning visitor may have the id from an older build that never stored the
 * object, and re-prompting them would be wrong. Pages resolve the object from
 * the clinic list when it is missing.
 */
export const hasSelectedClinic = () => !!getStoredClinicId();

/**
 * Persist a clinic. Ignores anything without a real id so a placeholder can
 * never overwrite a good selection. Returns whether it was stored.
 */
export const setStoredClinic = (clinic) => {
  if (!isValidClinicId(clinic?.id)) return false;
  localStorage.setItem(ID_KEY, String(clinic.id));
  localStorage.setItem(CLINIC_KEY, JSON.stringify(clinic));
  return true;
};

export const clearStoredClinic = () => {
  localStorage.removeItem(ID_KEY);
  localStorage.removeItem(CLINIC_KEY);
};

/**
 * Adopt the account's default clinic after sign-in, but never over a location
 * the visitor picked themselves — a guest who chose Calgary NE and then logged
 * in should stay on Calgary NE, not be moved to whatever the account defaults
 * to. Returns whether it was stored.
 */
export const adoptDefaultProvider = (provider) => {
  if (hasSelectedClinic()) return false;
  return setStoredClinic(provider);
};

/**
 * Clear the "undefined" / "null" strings written by earlier builds. Called once
 * at app start so returning visitors are not stuck with a selection that looks
 * present but resolves to nothing (and so the bare JSON.parse calls still left
 * in older screens don't throw).
 */
export const pruneInvalidStoredClinic = () => {
  const rawId = localStorage.getItem(ID_KEY);
  if (rawId !== null && !isValidClinicId(rawId)) localStorage.removeItem(ID_KEY);

  const rawClinic = localStorage.getItem(CLINIC_KEY);
  if (rawClinic !== null && !getStoredClinic()) localStorage.removeItem(CLINIC_KEY);
};
